// Learn (F1; mockup l01): today's goal as a ring, then the tracks the child's controls switch
// on, each with how much is learned. A session is free and 5 cards long; reaching the goal earns
// a play for the Arcade (domain/arcade/plays). With the world stage live, a slot under the cards
// shows the 3D Learn island (stage/sets/learnSet; owner's pick 2026-10-13); in Calm/Still or
// without WebGL it's the flat screen.
import { useLayoutEffect, useMemo, useRef } from 'react'
import type { Person } from '../../shared/events'
import type { LearnTrack } from '../../shared/controls'
import type { Pack } from '../../shared/pack/schema'
import { openOverlay } from '../app/router'
import { startLearn, startWarmup, useArcade } from '../arcade/useArcade'
import { todaysNewSounds } from '../domain/newSounds'
import { rightOn } from '../domain/arcade/plays'
import { streak } from '../domain/arcade/progress'
import { lookup } from '../domain/arcade/cards'
import { LEVEL_XP, levelProgress } from '../domain/arcade/level'
import { enabledTracks, islandGlyphs, scriptTitle, trackGlyph, trackInfo, tracksFor, type Track } from '../domain/learn'
import { addDays } from '../domain/time'
import { figureFor } from '../../shared/figures'
import { useSession } from '../data/session'
import { useStage } from '../stage/bridge'
import { t } from '../strings/en'
import { contentLang, say } from '../audio/tts'

// Badge and bar colours by kind; a second script (hiragana beside katakana) gets its own.
const BADGE: Record<string, { bg: string; fg?: string }> = {
  everyday: { bg: 'linear-gradient(180deg,#7CE6B8,var(--mint))' },
  // White glyphs: the gradients end on the dark tones and the glyph is ink-edged (WI-29, L6-04).
  script: { bg: 'linear-gradient(180deg,var(--red),var(--red-d))', fg: '#fff' },
  script2: { bg: 'linear-gradient(180deg,var(--purple, #9B6BFF),#6B3FD6)', fg: '#fff' },
  together: { bg: 'linear-gradient(180deg,var(--blue),var(--blue-d))', fg: '#fff' },
  numbers: { bg: 'linear-gradient(180deg,#FFE58A,var(--yellow))' },
}
const BAR: Record<string, string> = { everyday: 'var(--mint)', script: 'var(--red)', script2: 'var(--purple, #9B6BFF)', together: 'var(--blue)', numbers: 'var(--yellow)' }
const look = (tr: Track) => (tr.kind === 'script' && tr.index ? 'script2' : tr.kind)

/** A small speaker on a new sound's tile (WI-09). */
function SpeakerMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 9h4l5-4v14l-5-4H4z" fill="var(--ink)" />
      <path d="M16 8.5a5 5 0 0 1 0 7" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}

/** A locked track's badge (K9): a chunky ink padlock, drawn in the app's line style, not an emoji. */
function LockIcon({ size = 26 }: { size?: number }) {
  return (
    <svg className="learn-lock" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M7.5 10.5V8a4.5 4.5 0 0 1 9 0v2.5" fill="none" stroke="var(--ink)" strokeWidth="2.6" strokeLinecap="round" />
      <rect x="4" y="10" width="16" height="11.5" rx="3" fill="var(--ink)" />
      <rect x="5.6" y="11.4" width="12.8" height="2" rx="1" fill="#fff" opacity=".25" />
      <circle cx="12" cy="15.4" r="1.7" fill="#fff" />
      <path d="M12 16.2v2.4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

/** Today's goal as a ring (mockups l01, l03). */
export function GoalRing({ done, goal, size = 92 }: { done: number; goal: number; size?: number }) {
  const r = 36
  const c = 2 * Math.PI * r
  const k = Math.min(1, goal ? done / goal : 0)
  return (
    <svg width={size} height={size} viewBox="0 0 92 92" role="img" aria-label={t.learn.goalLabelOf(Math.min(done, goal), goal)}>
      <circle cx="46" cy="46" r={r} fill="none" stroke="#EAF4FF" strokeWidth="14" />
      {k > 0 ? <circle cx="46" cy="46" r={r} fill="none" stroke="var(--mint)" strokeWidth="14" strokeDasharray={`${c * k} ${c}`} strokeLinecap="round" transform="rotate(-90 46 46)" /> : null}
      <circle cx="46" cy="46" r="43" fill="none" stroke="var(--ink)" strokeWidth="3" />
      <circle cx="46" cy="46" r="29" fill="none" stroke="var(--ink)" strokeWidth="3" />
      <text x="46" y="53" textAnchor="middle" fontFamily="Fredoka" fontWeight="700" fontSize={goal >= 10 ? 18 : 20} fill="var(--ink)">
        {Math.min(done, goal)}/{goal}
      </text>
    </svg>
  )
}

export function LearnScreen({ pack, person }: { pack: Pack; person: Person }) {
  const { ctx, day, events, settings, rules, progress, restDays } = useArcade(pack, person.id)
  // The trip's tracks (from its language kit, WI-10) that the child's controls switch on.
  const tracks = settings ? enabledTracks(pack, settings.learn.tracks) : tracksFor(pack)
  const infos = tracks.map((tr) => trackInfo(tr, ctx))
  const done = rightOn(events, person.id, day)
  const goal = settings?.learn.goal
  const days = streak(progress, day, addDays(day, -1), restDays)
  const sessionsToday = events.filter((e) => e.type === 'learn.completed' && e.personId === person.id && (e.payload as { day?: string }).day === day).length
  // The button goes on with the track played last today, else the first one with cards left
  // (L2-10: it used to be Everyday, first in TRACKS, every time).
  const lastTrack = events
    .filter((e) => e.type === 'card.answered' && e.personId === person.id && (e.payload as { day?: string }).day === day)
    .reduce<{ at: number; track?: string }>((best, e) => (e.at >= best.at && (e.payload as { track?: string }).track ? { at: e.at, track: (e.payload as { track?: string }).track } : best), { at: 0 }).track
  const last = infos.find((i) => i.open && i.track.id === lastTrack)
  const next = last ?? infos.find((i) => i.open && i.learned < i.total) ?? infos.find((i) => i.open)
  const cta = (tr: Track): string =>
    goal !== undefined && done >= goal ? t.learn.practise : done === 0 ? t.learn.start(short(tr)) : t.learn.continue(short(tr), goal !== undefined ? goal - done : undefined)
  // The button names the track short ("Katakana"), so it stays one line at phone width.
  const short = (tr: Track): string => (tr.kind === 'script' ? (scriptTitle(pack, tr.id) ?? title(tr)) : title(tr))
  const title = (tr: Track): string => (tr.kind === 'script' ? t.learn.script(scriptTitle(pack, tr.id)) : (t.learn.tracks[tr.kind] ?? tr.kind))
  const firstScript = scriptTitle(pack)
  // XP and level (WI-10): the bar moves with every right answer.
  const nextAt = LEVEL_XP[progress.level] as number | undefined
  // Sound words popped on the island (progress.sfxSeen), whatever deck the kit keeps them in.
  const sounds = useMemo(
    () => progress.sfxSeen.flatMap((id) => {
      const card = lookup(pack, id)?.card
      return card?.kind === 'reading' ? [{ id, word: card.script, meaning: card.meaning, say: card.say ?? card.script }] : []
    }),
    [pack, progress.sfxSeen],
  )

  // The 3D island: the next characters to learn, the person's own figure, and its slot.
  // WI-34: 3D in every motion tier (Calm and Still get a still frame: learnSet.setTier).
  const stage3d = useStage((s) => s.status === 'live')
  const slot = useRef<HTMLDivElement>(null)
  const people = useSession((s) => s.people)
  const glyphs = useMemo(() => islandGlyphs(pack, progress), [pack, progress])
  const lookKey = JSON.stringify([glyphs, figureFor(person, people ?? [])])
  useLayoutEffect(() => {
    if (!stage3d) return
    const [g, figure] = JSON.parse(lookKey) as [typeof glyphs, ReturnType<typeof figureFor>]
    const measure = () => {
      const r = slot.current?.getBoundingClientRect()
      if (r) useStage.getState().set({ learn: { frame: { top: r.top, bottom: r.bottom }, ...g, figure } })
    }
    measure()
    const ro = new ResizeObserver(measure)
    if (slot.current) ro.observe(slot.current)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [stage3d, lookKey])

  // WI-09: today's new sounds (the first script's next characters), when its track is on and open.
  const scriptInfo = infos.find((i) => i.open && i.track.kind === 'script' && !i.track.index)
  const newSounds = scriptInfo ? todaysNewSounds(pack, progress) : []
  const warmUp = () => {
    if (!scriptInfo) return
    const started = startWarmup(ctx, scriptInfo.track.id, newSounds, settings?.learn.difficulty ?? 'standard', person.id)
    if (started) openOverlay(`session:${started.key}`)
  }

  const go = (tr: LearnTrack) => {
    const started = startLearn(ctx, tr, sessionsToday, settings?.learn.difficulty ?? 'standard', person.id)
    if (started) openOverlay(`session:${started.key}`)
  }

  return (
    <main className={`screen learn${stage3d ? ' on-stage' : ''}`}>
      <div className="screen-head">
        <h1 className="h1">{t.learn.title}</h1>
        {/* A streak is two days or more (WI-11): "1 day" on the first day says nothing. */}
        {days >= 2 ? <span className="pill">{t.learn.streak(days)}</span> : null}
      </div>
      {goal !== undefined ? (
        <section className="panel learn-goal">
          <GoalRing done={done} goal={goal} />
          <div>
            <b>{t.learn.goalTitle}</b>
            <small>{done >= goal ? t.learn.goalDone : rules.daily.on ? t.learn.goalLeft(goal - done) : t.learn.goalLeftNoPlay(goal - done)}</small>
            {rules.daily.on ? <span className={`tag ${done >= goal ? 'mint' : 'yellow'}`}>{done >= goal ? t.learn.earned : t.learn.earnsPlay(rules.daily.plays)}</span> : null}
          </div>
        </section>
      ) : null}
      <div
        className="learn-xp"
        role="progressbar"
        aria-label={t.learn.xpLabel(progress.level, progress.xp, nextAt)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(levelProgress(progress.xp) * 100)}
      >
        <span className="tag yellow">{t.learn.level(progress.level)}</span>
        <span className="learn-bar">
          <i style={{ width: `${levelProgress(progress.xp) * 100}%`, background: 'var(--yellow)' }} />
        </span>
        <small>{t.learn.xp(progress.xp, nextAt)}</small>
      </div>
      {newSounds.length ? (
        <section className="panel learn-new" aria-label={t.learn.newSounds}>
          <div className="learn-new-head">
            <b>{t.learn.newSounds}</b>
            <span className="tag yellow">{t.learn.newSoundsCount(newSounds.length)}</span>
          </div>
          <div className="learn-new-row" lang={contentLang()}>
            {newSounds.map((s) => (
              <button key={s.cardId} className="learn-new-char press" aria-label={t.learn.newSoundHear(s.ch)} onClick={() => say(s.say)}>
                {s.ch}
                <i aria-hidden>
                  <SpeakerMark />
                </i>
              </button>
            ))}
          </div>
          <small>{t.learn.newSoundsSub}</small>
          <button className="btn y press" onClick={warmUp}>
            {t.learn.warmUp(newSounds.length)}
          </button>
        </section>
      ) : null}
      <p className="mono sheet-sub">{t.learn.tracksHead}</p>
      <div className="panel sheet-panel">
        {infos.map((i) => {
          const tr = i.track
          const k = look(tr)
          return (
            <button key={tr.id} className="row row-btn press learn-track" disabled={!i.open} aria-label={title(tr)} onClick={() => go(tr.id)}>
              <span className={`learn-badge${i.open && BADGE[k]!.fg ? ' on-colour' : ''}`} lang={contentLang()} style={{ background: i.open ? BADGE[k]!.bg : '#C9D6E4', color: BADGE[k]!.fg ?? 'var(--ink)' }}>
                {i.open ? trackGlyph(pack, tr) : <LockIcon />}
              </span>
              <span className="learn-track-text">
                <b>{title(tr)}</b>
                {i.open && t.learn.blurbs[tr.kind] ? <small className="learn-blurb">{t.learn.blurbs[tr.kind]}</small> : null}
                <small aria-label={i.open ? t.learn.progressLabel(title(tr), i.met, i.learned, i.total) : undefined}>
                  {i.open ? t.learn.progress(i.met, i.learned, i.total) : tr.kind === 'together' ? t.learn.unlocksAt(i.opensAt ?? 0) : t.learn.scriptUnlocksAt(i.opensAt ?? 0, firstScript)}
                </small>
                {i.open ? (
                  // Met (light) under learned (solid): both move, from the first right answer on.
                  <span className="learn-bar">
                    <i className="met" style={{ width: `${(i.met / Math.max(1, i.total)) * 100}%`, background: BAR[k] }} />
                    <i style={{ width: `${(i.learned / Math.max(1, i.total)) * 100}%`, background: BAR[k] }} />
                  </span>
                ) : null}
              </span>
              {i.open ? <span className="chev">›</span> : null}
            </button>
          )
        })}
        {!infos.length ? (
          <div className="row">
            <small>{t.learn.noTracks}</small>
          </div>
        ) : null}
      </div>
      {next ? (
        <button className="btn y press learn-go" onClick={() => go(next.track.id)}>
          {cta(next.track)}
        </button>
      ) : null}
      <p className="caption">{t.learn.free}</p>
      {sounds.length ? (
        <>
          <p className="mono sheet-sub">{t.learn.soundsHead}</p>
          <section className="panel learn-sounds" aria-label={t.learn.soundsHead}>
            <small>{t.learn.soundsSub}</small>
            <div className="learn-sound-row">
              {sounds.map((s) => (
                <button key={s.id} className="learn-sound press" aria-label={t.learn.soundHear(s.word, s.meaning)} onClick={() => say(s.say)}>
                  <b lang={contentLang()}>{s.word}</b>
                  <small>{s.meaning}</small>
                </button>
              ))}
            </div>
          </section>
        </>
      ) : null}
      {stage3d ? <div ref={slot} className="learn-slot" role="img" aria-label={t.learn.island} /> : null}
    </main>
  )
}
